import { useEffect, useState } from "react";

const REVEAL_MARGIN = 16;

const scrollParent = (from: HTMLElement, root: HTMLElement) => {
  for (let node = from.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    if (node === root) return null;
  }
  return null;
};

const revealFocused = (root: HTMLElement) => {
  const focused = document.activeElement;
  if (
    !(focused instanceof HTMLElement) ||
    !focused.matches("input, textarea") ||
    !root.contains(focused)
  ) {
    return;
  }
  const scroller = scrollParent(focused, root);
  if (!scroller) return;

  const box = scroller.getBoundingClientRect();
  const target = focused.getBoundingClientRect();
  if (target.bottom > box.bottom - REVEAL_MARGIN) {
    scroller.scrollTop += target.bottom - box.bottom + REVEAL_MARGIN;
  } else if (target.top < box.top + REVEAL_MARGIN) {
    scroller.scrollTop -= box.top + REVEAL_MARGIN - target.top;
  }
};

// Replaces vaul's repositionInputs, which ignores visualViewport.offsetTop and
// lifts the drawer twice when the browser has already panned for the keyboard.
// Pass the returned ref to a bottom Drawer.Content with the `trade__drawer`
// class; descendants read `--drawer-max-height` to stay above the keyboard.
export const useKeyboardAwareDrawer = () => {
  const [el, setEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!el || !vv) return;

    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      el.style.setProperty("--keyboard-inset", `${inset}px`);
      el.style.setProperty(
        "--drawer-max-height",
        `calc(${vv.height}px - 1rem)`
      );
      revealFocused(el);
    };
    const onFocusIn = () => requestAnimationFrame(() => revealFocused(el));

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    el.addEventListener("focusin", onFocusIn);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      el.removeEventListener("focusin", onFocusIn);
    };
  }, [el]);

  return setEl;
};
